import React from 'react'
import { Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { TStorageItemRS, TModuleOptions } from '../../../../models/storageModel'
import { calculateInventorySummary } from '../../../../utils/reportUtils'
import { TExcelColumn } from '../../../../utils/excelExport'
import { textNumberFormater } from '../../../../utils'
import InventorySummaryCards from './InventorySummaryCards'

const { Text } = Typography

interface CurrentStockReportProps {
  data: TStorageItemRS[]
  storehouses: any[]
  categoryOptions: TModuleOptions[]
  selectedStorehouseUid: string
  loading?: boolean
}

export function getCurrentStockExcelColumns(storehouses: any[]): TExcelColumn[] {
  const cols: TExcelColumn[] = [
    { header: '#', key: 'index', width: 8, format: 'number' },
    { header: 'Código', key: 'code', width: 18 },
    { header: 'Producto / Servicio', key: 'name', width: 30 },
    { header: 'Tipo', key: 'registerType', width: 14 },
    { header: 'Categoría', key: 'categoryName', width: 22 },
    { header: 'Presentación', key: 'display', width: 14 }
  ]

  storehouses.forEach(st => {
    cols.push({
      header: `Stock ${st.name || st.alias || st.uid}`,
      key: `stock_${st.uid}`,
      width: 16,
      format: 'number'
    })
  })

  cols.push(
    { header: 'Existencia Total', key: 'totalQuantity', width: 16, format: 'number' },
    { header: 'Precio Compra', key: 'purchasePrice', width: 16, format: 'money' },
    { header: 'Precio Venta', key: 'sellPrice', width: 16, format: 'money' }
  )

  return cols
}

export function getCurrentStockExcelData(
  data: TStorageItemRS[],
  storehouses: any[],
  categoryOptions: TModuleOptions[]
) {
  const getCategoryName = (catKey: string) => {
    const found = categoryOptions.find(opt => opt.key === catKey)
    return found?.string_value || catKey || ''
  }

  return data.map((item, i) => {
    const isService = item.register_type === 'SERVICE'
    const totalQty = isService
      ? 0
      : (item.inventory && Array.isArray(item.inventory))
      ? item.inventory.reduce((sum, inv) => sum + (inv.quantity || 0), 0)
      : (item.quantity || 0)

    const row: any = {
      index: i + 1,
      code: item.custom_id || item.sku || String(item.id),
      name: item.name,
      registerType: isService ? 'Servicio' : 'Producto',
      categoryName: getCategoryName(item.category),
      display: item.display || 'UND',
      totalQuantity: totalQty,
      purchasePrice: item.purchase_price || 0,
      sellPrice: item.sell_price || 0
    }

    storehouses.forEach(st => {
      const invEntry = item.inventory?.find(inv => inv.uid === st.uid || inv.id === st.id)
      row[`stock_${st.uid}`] = isService ? 'N/A' : (invEntry?.quantity || 0)
    })

    return row
  })
}

export const CurrentStockReport: React.FC<CurrentStockReportProps> = ({
  data,
  storehouses,
  categoryOptions,
  selectedStorehouseUid,
  loading = false
}) => {
  const summary = calculateInventorySummary(data, storehouses)

  const getCategoryName = (catKey: string) => {
    const found = categoryOptions.find(opt => opt.key === catKey)
    return found?.string_value || catKey || ''
  }

  const columns: ColumnsType<TStorageItemRS> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Código',
      key: 'code',
      width: 130,
      render: (_, record) => <code>{record.custom_id || record.sku || String(record.id)}</code>
    },
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Tipo',
      dataIndex: 'register_type',
      key: 'register_type',
      width: 100,
      render: (type) => (
        <Tag color={type === 'SERVICE' ? 'orange' : 'blue'}>
          {type === 'SERVICE' ? 'Servicio' : 'Producto'}
        </Tag>
      )
    },
    {
      title: 'Categoría',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (cat) => getCategoryName(cat) || '-'
    },
    {
      title: 'Presentación',
      dataIndex: 'display',
      key: 'display',
      width: 110,
      render: (disp) => disp || 'UND'
    }
  ]

  // Agregar columnas por almacén
  storehouses.forEach(st => {
    columns.push({
      title: `Stock ${st.name || st.alias || st.uid}`,
      key: `st_${st.uid}`,
      width: 130,
      align: 'right',
      render: (_, record) => {
        if (record.register_type === 'SERVICE') return <Tag color="default">N/A</Tag>
        const inv = record.inventory?.find(i => i.uid === st.uid || i.id === st.id)
        const qty = inv ? inv.quantity : 0
        return <Text style={{ color: qty <= 0 ? '#ff4d4f' : '#52c41a' }}>{textNumberFormater(qty)}</Text>
      }
    })
  })

  columns.push(
    {
      title: 'Existencia Total',
      key: 'total_quantity',
      width: 140,
      align: 'right',
      sorter: (a, b) => {
        const getQty = (item: TStorageItemRS) => (item.inventory ? item.inventory.reduce((sum, i) => sum + i.quantity, 0) : item.quantity || 0)
        return getQty(a) - getQty(b)
      },
      render: (_, record) => {
        if (record.register_type === 'SERVICE') return <Tag color="default">N/A</Tag>
        const qty = record.inventory
          ? record.inventory.reduce((sum, i) => sum + i.quantity, 0)
          : record.quantity || 0
        return <Text strong style={{ color: qty <= 0 ? '#ff4d4f' : '#1890ff', fontSize: '0.95rem' }}>{textNumberFormater(qty)}</Text>
      }
    },
    {
      title: 'Precio Venta',
      dataIndex: 'sell_price',
      key: 'sell_price',
      width: 130,
      align: 'right',
      render: (val) => `$ ${textNumberFormater(val || 0)}`
    }
  )

  return (
    <div>
      <InventorySummaryCards summary={summary} loading={loading} />

      <div style={{ overflowX: 'auto', width: '100%' }}>
        <Table<TStorageItemRS>
          columns={columns}
          dataSource={data.map((item, i) => ({ ...item, key: item.uid || String(i) }))}
          loading={loading}
          pagination={{
            defaultPageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ['15', '30', '50', '100'],
            showTotal: (total) => `Total ${total} elementos`
          }}
          size="small"
          bordered
          scroll={{ x: 'max-content' }}
        />
      </div>
    </div>
  )
}

export default CurrentStockReport
